import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Send, Star } from "lucide-react";

const blank = () => ({
  full_name: "",
  is_anonymous: false,
  star_rating: 0,
  enjoyed_most: "",
  what_changed: "",
  would_recommend: "Yes",
  additional_comments: "",
  share_publicly: true,
});

const StarRow = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5" data-testid="manual-f-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} className="transition hover:scale-110" data-testid={`manual-f-star-${n}`}>
        <Star className={`w-7 h-7 ${n <= value ? "fill-[#D4A547] text-[#D4A547]" : "text-[#EBE3D2]"}`} strokeWidth={1.5} />
      </button>
    ))}
  </div>
);

/**
 * Admin shortcut dialog — facilitator manually enters end-of-programme feedback on behalf of a participant.
 */
const ManualFeedbackDialog = ({ open, onOpenChange, workshop, onSubmitted }) => {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setForm(blank()); }, [open]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.star_rating === 0) { toast.error("Star rating is required"); return; }
    if (!form.is_anonymous && !form.full_name.trim()) { toast.error("Name required (or check 'Anonymous')"); return; }
    setBusy(true);
    try {
      await api.post("/public/feedback", {
        workshop_id: workshop.id,
        organisation: workshop.client_name || "",
        ...form,
      });
      toast.success("Feedback saved");
      if (onSubmitted) onSubmitted();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="manual-feedback-dialog">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold flex items-center gap-2">
            <UserPlus className="w-3 h-3" /> Facilitator entry
          </div>
          <DialogTitle className="font-display text-2xl">Add end-of-programme feedback</DialogTitle>
          <div className="text-xs text-[#6B5944]">
            For <strong>{workshop?.name}</strong>{workshop?.client_name ? ` · ${workshop.client_name}` : ""}.
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-xs font-medium">Participant name {form.is_anonymous && <span className="text-[#6B5944]">(anonymous)</span>}</Label>
            <Input
              data-testid="manual-f-name"
              value={form.full_name}
              disabled={form.is_anonymous}
              onChange={(e) => setF("full_name", e.target.value)}
              className="h-10 disabled:opacity-50"
              placeholder={form.is_anonymous ? "(Submitting anonymously)" : "First and last name"}
            />
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox data-testid="manual-f-anonymous" checked={form.is_anonymous} onCheckedChange={(v) => setF("is_anonymous", !!v)} />
              <span>Submit anonymously</span>
            </label>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium">Overall rating *</Label>
            <StarRow value={form.star_rating} onChange={(v) => setF("star_rating", v)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">What did you enjoy most?</Label>
              <Textarea data-testid="manual-f-enjoy" rows={3} value={form.enjoyed_most} onChange={(e) => setF("enjoyed_most", e.target.value)} className="rounded-lg" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">What has changed for you?</Label>
              <Textarea data-testid="manual-f-change" rows={3} value={form.what_changed} onChange={(e) => setF("what_changed", e.target.value)} className="rounded-lg" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Would recommend?</Label>
              <Select value={form.would_recommend} onValueChange={(v) => setF("would_recommend", v)}>
                <SelectTrigger data-testid="manual-f-recommend" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="Maybe">Maybe</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Sharing permission</Label>
              <label className="flex items-center gap-2 text-sm h-10 cursor-pointer">
                <Checkbox data-testid="manual-f-share" checked={form.share_publicly} onCheckedChange={(v) => setF("share_publicly", !!v)} />
                <span>OK to feature publicly</span>
              </label>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Additional comments</Label>
            <Textarea data-testid="manual-f-comments" rows={3} value={form.additional_comments} onChange={(e) => setF("additional_comments", e.target.value)} className="rounded-lg" />
          </div>
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="manual-f-submit">
            <Send className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "Save feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManualFeedbackDialog;
